import { Fragment } from "react";
import { channelName, type DeliveryState, type Escalation, type EscalationLevel } from "@/hooks/use-escalations";

/** Small pieces shared by the P0 banner, the Settings -> Notifications card and the Draper thread card. */

const LEVEL_CLASS: Record<EscalationLevel, string> = {
  P0: "border-red-300/60 bg-red-500/30 text-red-50",
  P1: "border-orange-300/50 bg-orange-500/20 text-orange-100",
  info: "border-border bg-muted/40 text-foreground/80",
};

export function LevelChip({ level, className = "" }: { level: EscalationLevel; className?: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold ${LEVEL_CLASS[level]} ${className}`}
      data-testid={`level-${level}`}
    >
      {level === "info" ? "Info" : level}
    </span>
  );
}

/** "banner" marks sit on the dark red P0 banner; "plain" marks sit on an ordinary card. */
const MARK: Record<"banner" | "plain", Record<DeliveryState, { glyph: string; sr: string; cls: string }>> = {
  banner: {
    delivered: { glyph: "✓", sr: "delivered", cls: "font-bold text-emerald-300" },
    failed: { glyph: "✗", sr: "failed", cls: "font-extrabold text-red-100 underline decoration-red-300/70 underline-offset-2" },
    pending: { glyph: "…", sr: "still sending", cls: "font-bold text-amber-200" },
    skipped: { glyph: "–", sr: "not set up", cls: "font-bold text-red-100/70" },
  },
  plain: {
    delivered: { glyph: "✓", sr: "delivered", cls: "font-bold text-emerald-400" },
    failed: { glyph: "✗", sr: "failed", cls: "font-extrabold text-red-400" },
    pending: { glyph: "…", sr: "still sending", cls: "font-bold text-amber-400" },
    skipped: { glyph: "–", sr: "not set up", cls: "font-bold text-muted-foreground" },
  },
};

/** "delivery: Telegram ✓ / email ✗". The tick and cross never carry the meaning alone: each has a screen-reader word. */
export function DeliveryLine({ escalation, className = "", tone = "banner" }: { escalation: Escalation; className?: string; tone?: "banner" | "plain" }) {
  if (escalation.channels.length === 0) {
    return (
      <span className={className} data-testid="delivery-line" data-delivery="none">
        delivery: not tried yet
      </span>
    );
  }
  return (
    <span className={className} data-testid="delivery-line">
      delivery:{" "}
      {escalation.channels.map((c, i) => {
        const m = MARK[tone][c.state];
        return (
          <Fragment key={`${c.channel}-${i}`}>
            {i > 0 && <span aria-hidden="true"> / </span>}
            <span title={c.detail ?? c.status} data-channel={c.channel} data-state={c.state}>
              {channelName(c.channel)} <span className={m.cls} aria-hidden="true">{m.glyph}</span>
              <span className="sr-only"> {m.sr}</span>
            </span>
          </Fragment>
        );
      })}
    </span>
  );
}
